/* Player grouping helpers for the mobile ledger.
 *
 * Mirrors the status logic already used in `components/PlayerStats.jsx`
 * (cashed-out / unsettled), extracted so the full-width tiles and the
 * All / Owing / Cashed-out tabs share one source of truth.
 */

import { formatMoney } from "../../../utilities/common";

const txnType = (t) => (t.type ?? "").toUpperCase();
const isCover = (t) => txnType(t) === "COVER";
const isCashOut = (t) => txnType(t) === "CASH_OUT" || isCover(t);

/** A player has cashed out once any CASH_OUT/COVER transaction exists. */
export function isCashedOut(player) {
    return (player.transactions ?? []).some(isCashOut);
}

/** Buy-ins that haven't been marked settled (still on the books). */
export function unsettledBuyIns(player) {
    return (player.transactions ?? []).filter(
        (t) => t.type === "BUY_IN" && !t.isSettled,
    );
}

/**
 * Unsettled buy-ins with their remaining owed after covers are applied.
 * Cashing out pays buy-ins down with `cover` transactions without settling
 * them, so the host can settle the remainder later. Covers aren't linked to
 * a specific buy-in, so the total cover credit is pooled and applied to
 * unsettled buy-ins oldest-first — independent of payment method, since a
 * buy-in's method can be edited after a cover is recorded (which would
 * otherwise orphan the cover and overstate what's owed). Returns
 * `{ transaction, owed }` per unsettled buy-in.
 */
export function outstandingBuyIns(player) {
    const txns = player.transactions ?? [];
    let coverPool = txns.reduce(
        (sum, t) => (isCover(t) ? sum + (t.amount ?? 0) : sum),
        0,
    );
    const result = [];
    for (const t of txns) {
        if (t.type !== "BUY_IN" || t.isSettled) continue;
        const applied = Math.min(coverPool, t.amount ?? 0);
        coverPool -= applied;
        result.push({ transaction: t, owed: (t.amount ?? 0) - applied });
    }
    return result;
}

/** Dollar amount a player still owes the table (buy-ins net of covers). */
export function owedAmount(player) {
    return outstandingBuyIns(player).reduce((sum, b) => sum + Math.max(0, b.owed), 0);
}

/** A player is "owing" while their buy-ins aren't fully covered/settled. */
export function isOwing(player) {
    return owedAmount(player) > 0;
}

/**
 * The house owes the player a payout — they have an unsettled cash-out
 * (e.g. a digital payout that hasn't been sent yet). This is the opposite
 * direction from `isOwing`: "Owing" players owe the table, "Owed" players
 * are owed by the house.
 */
export function isOwedPayout(player) {
    return (player.transactions ?? []).some(
        (t) => txnType(t) === "CASH_OUT" && !t.isSettled,
    );
}

/** Dollar amount the house still owes the player — sum of unsettled
 * cash-outs (payouts not yet sent). */
export function owedPayoutAmount(player) {
    return (player.transactions ?? [])
        .filter((t) => txnType(t) === "CASH_OUT" && !t.isSettled)
        .reduce((sum, t) => sum + (t.amount ?? 0), 0);
}

/**
 * One settlement status for a player row, shared by the mobile tiles and the
 * desktop rows. Composes the cashed-out state with the money direction so a
 * cashed-out player who still owes the table (or is still owed a payout) reads
 * as e.g. "cashed out · owes $40" rather than hiding the cashed-out state
 * behind the amount. `tone` drives the status pip and the row highlight:
 *   owing   — owes the table (money still in)        [warn]
 *   owed    — the house owes them a payout           [gold]
 *   settled — cashed out and square                  [up]
 *   active  — still in the game, nothing outstanding [up]
 *   empty   — no transactions yet                    [neutral]
 */
export function playerStatus(player) {
    const owes = owedAmount(player);
    const owedOut = owedPayoutAmount(player);
    const cashedOut = isCashedOut(player);
    const hasTxns = (player.transactions ?? []).length > 0;

    let tone;
    let standing;
    if (owes > 0) {
        tone = "owing";
        standing = `owes ${formatMoney(owes)}`;
    } else if (owedOut > 0) {
        tone = "owed";
        standing = `owed ${formatMoney(owedOut)}`;
    } else if (cashedOut) {
        tone = "settled";
        standing = "settled";
    } else if (hasTxns) {
        tone = "active";
        standing = "all settled";
    } else {
        tone = "empty";
        standing = "no transactions yet";
    }

    const label = cashedOut ? `cashed out · ${standing}` : standing;

    return { tone, cashedOut, owes, owedOut, standing, label };
}

export const GROUPS = [
    { key: "all", label: "All" },
    { key: "owing", label: "Owing" },
    { key: "cashedOut", label: "Cashed out" },
    { key: "owed", label: "Owed" },
];

const MATCHERS = {
    all: () => true,
    owing: isOwing,
    cashedOut: isCashedOut,
    owed: isOwedPayout,
};

/** Filter a roster to a group key (all | owing | cashedOut | owed). */
export function filterByGroup(players, groupKey) {
    const match = MATCHERS[groupKey] ?? MATCHERS.all;
    return players.filter(match);
}

/** Count per group, for the tab badges. */
export function groupCounts(players) {
    return {
        all: players.length,
        owing: players.filter(isOwing).length,
        cashedOut: players.filter(isCashedOut).length,
        owed: players.filter(isOwedPayout).length,
    };
}

/** Total still on the table across the whole roster (players owe this). */
export function totalOnTable(players) {
    return players.reduce((sum, p) => sum + owedAmount(p), 0);
}

/** Total the house owes players across the roster (unsettled payouts). */
export function totalOwedPayout(players) {
    return players.reduce((sum, p) => sum + owedPayoutAmount(p), 0);
}

/**
 * Empty-state copy for the roster, shared by the web and mobile ledgers so
 * they read identically. `total` is the full roster size, `query` the
 * current search text (already trimmed).
 */
export function emptyText(groupKey, total, query) {
    if (total === 0) return "No players yet — add one to get started.";
    if (query) return `No players match “${query}”.`;
    if (groupKey === "owing") return "Nobody owes the table. ✓";
    if (groupKey === "cashedOut") return "No one has cashed out yet.";
    if (groupKey === "owed") return "Everyone's square — nothing outstanding. ✓";
    return "No players match.";
}
